import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { translations } from '../../../configs/translations'
import { PlayerRace } from '../../../models/PlayerRace'
import RaceService from '../../../services/RaceService'
import { ClientApi } from '../../../services/clientApi'
import { useStore } from '../../../store/store'
import { NuiMessages } from '../../../utils/ClientEvents'
import './Finish.css'
import { Leaderboard } from './leaderboard/Leaderboard'
import { MainFinish } from './mainfinish/MainFinish'

type FinishStage = 'idle' | 'main' | 'gap' | 'leaderboard'

export const Finish = () => {
    const language = useStore((state) => state.language)
    const setRaceFramesVisible = useStore((state) => state.setRaceFramesVisible)
    const t = translations[language]

    const [raceId, setRaceId] = useState<string | null>(null)
    const [raceData, setRaceData] = useState<PlayerRace | null>(null)
    const [stage, setStage] = useState<FinishStage>('idle')
    const [isContinued, setIsContinued] = useState(false)

    const isContinueSent = useRef(false)

    const showError = useCallback((error: any) => {
        const errorKey = error?.response?.data as keyof typeof t | undefined

        toast.error(
            errorKey && t[errorKey] ? t[errorKey] : t.error_unknown,
            { className: 'notification-error' }
        )
    }, [t])

    useEffect(() => {
        ClientApi.on(NuiMessages.FINISH, ({ raceId }: { raceId: string }) => {
            isContinueSent.current = false
            setRaceData(null)
            setStage('idle')
            setIsContinued(false)
            setRaceId(raceId)
        })
    }, [])

    useEffect(() => {
        if (!raceId) return

        RaceService.getPlayerRaceDataById(raceId)
            .then((race) => {
                setRaceData(race)
            })
            .catch(showError)
    }, [raceId, showError])

    useEffect(() => {
        if (!raceData?.place || stage !== 'idle') return

        const timers = [
            setTimeout(() => setStage('main'), 3000),
            setTimeout(() => setStage('gap'), 8000),
            setTimeout(() => setStage('leaderboard'), 11000),
        ]

        return () => timers.forEach(clearTimeout)
    }, [raceData])

    useEffect(() => {
        if (!isContinued || isContinueSent.current) return

        isContinueSent.current = true
        setRaceFramesVisible(false)
        setRaceId(null)

        RaceService.sendContinue().catch(showError)
    }, [isContinued, showError, setRaceFramesVisible])

    const renderContent = () => {
        if (!raceData) return null

        if (stage === 'main') {
            return (
                <MainFinish
                    place={raceData.place}
                    mapName={raceData.mapName}
                    hours={raceData.hours}
                    minutes={raceData.minutes}
                    seconds={raceData.seconds}
                />
            )
        }

        if (stage === 'leaderboard') {
            return (
                <Leaderboard
                    mapName={raceData.mapName}
                    players={raceData.leaderboard}
                    isContinued={isContinued}
                    setIsContinued={setIsContinued}
                />
            )
        }

        return null
    }

    if (!raceId) return null

    return (
        <div className="finish">
            <div className="upper-frame-static"></div>

            <div className="finish-container">
                {renderContent()}
            </div>

            <div className="lower-frame-static"></div>
        </div>
    )
}
